import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Bell, Mail, Clock, CheckCircle, AlertCircle, Send, ArrowLeft, RefreshCw } from "lucide-react";
import { API } from "../App";
import { useI18n } from "../lib/i18n";

const RemindersDashboard = ({ user, logout }) => {
  const { t, locale } = useI18n();
  const [reminders, setReminders] = useState([]);
  const [stats, setStats] = useState({ total_reminders_sent: 0, reminders_today: 0, pending_reminders: 0 });
  const [loading, setLoading] = useState(true);
  const [sendingReminder, setSendingReminder] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [remindersRes, statsRes] = await Promise.all([
        axios.get(`${API}/therapist/reminders`, { withCredentials: true }),
        axios.get(`${API}/therapist/reminders/stats`, { withCredentials: true }),
      ]);
      setReminders(remindersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching reminders:", error);
      toast.error(t("reminders.errorLoading") || "Error loading reminders");
    } finally { setLoading(false); }
  };

  const sendManualReminder = async (assignmentId) => {
    setSendingReminder(assignmentId);
    try {
      await axios.post(`${API}/exercises/remind/${assignmentId}`, {}, { withCredentials: true });
      toast.success(t("reminders.sentSuccess"));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("reminders.errorSending"));
    } finally { setSendingReminder(null); }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString(locale === "fr" ? "fr-FR" : locale === "es" ? "es-ES" : "en-US", {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  const statCards = [
    { label: t("reminders.totalSent"), value: stats.total_reminders_sent, icon: Mail, color: "text-primary", bgColor: "bg-primary/10" },
    { label: t("reminders.today"), value: stats.reminders_today, icon: Bell, color: "text-accent", bgColor: "bg-accent/10" },
    { label: t("reminders.pendingReminders"), value: stats.pending_reminders, icon: AlertCircle, color: "text-amber-500", bgColor: "bg-amber-50" },
  ];

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6" data-testid="reminders-dashboard">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/therapist"><Button variant="ghost" size="icon"><ArrowLeft className="w-5 h-5" /></Button></Link>
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t("reminders.title")}</h1>
              <p className="text-muted-foreground text-sm sm:text-base mt-1">{t("reminders.subtitle")}</p>
            </div>
          </div>
          <Button onClick={fetchData} variant="outline" className="gap-2" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" /> {t("common.refresh") || "Refresh"}
          </Button>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          {statCards.map((stat, idx) => (
            <Card key={idx} className="card-hover">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div><p className="text-sm text-muted-foreground">{stat.label}</p><p className="text-3xl font-semibold mt-2">{stat.value}</p></div>
                  <div className={`p-3 rounded-lg ${stat.bgColor} ${stat.color}`}><stat.icon className="w-5 h-5" /></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Bell className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">{t("reminders.autoReminders")}</p>
                <p className="text-sm text-blue-700 mt-1">{t("reminders.autoRemindersDesc")}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Mail className="w-5 h-5" /> {t("reminders.history")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse flex items-center gap-4 p-4 border rounded-lg">
                    <div className="w-10 h-10 rounded-full bg-muted" /><div className="flex-1"><div className="h-4 bg-muted rounded w-48 mb-2" /><div className="h-3 bg-muted rounded w-32" /></div>
                  </div>
                ))}
              </div>
            ) : reminders.length > 0 ? (
              <div className="space-y-3">
                {reminders.map((reminder) => (
                  <div key={reminder.assignment_id} className="flex items-center gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors" data-testid={`reminder-row-${reminder.assignment_id}`}>
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-primary font-medium">{reminder.patient_name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium">{reminder.patient_name}</p>
                        <span className="text-muted-foreground">-</span>
                        <p className="text-sm text-muted-foreground truncate">{reminder.patient_email}</p>
                      </div>
                      <p className="text-sm text-primary font-medium mt-1">{reminder.exercise_title}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {t("reminders.assigned")}: {formatDate(reminder.created_at)}</span>
                        <span className="flex items-center gap-1"><Bell className="w-3 h-3" /> {t("reminders.lastReminder")}: {formatDate(reminder.last_reminder_sent)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${reminder.status === "completed" ? "bg-accent/10 text-accent" : "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300"}`}>
                        {reminder.status === "completed" ? (
                          <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3" /> {t("exercise.completed")}</span>
                        ) : (
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {t("exercise.pending")}</span>
                        )}
                      </div>
                      {reminder.status === "pending" && (
                        <Button size="sm" variant="outline" className="gap-1" onClick={() => sendManualReminder(reminder.assignment_id)} disabled={sendingReminder === reminder.assignment_id} data-testid={`send-reminder-${reminder.assignment_id}`}>
                          {sendingReminder === reminder.assignment_id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} {t("reminders.resend")}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Mail className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">{t("reminders.noReminders")}</p>
                <p className="text-sm text-muted-foreground mt-2">{t("reminders.autoSendDesc")}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default RemindersDashboard;
